import * as React from 'react'
import moment from 'moment'

import { OutputType } from 'app/constants/outputType'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { GroupModel } from 'app/models/groupModel'
import { isAdmin } from 'app/services/local/roleService'

import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminModal, AdminModalRow } from '../shared/AdminModal'

const MONTHS = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
]

type Props = {
  currentCompanyId: string
  companies: CompanyDetailsModel[]
  groups: GroupModel[]
  getCompanies: (
    tenantId?: string,
    includeDeleted?: boolean,
  ) => Promise<Array<CompanyDetailsModel>>
  getGroups: (tenantId?: string) => Promise<Array<GroupModel>>
  getActiveDriversReport: (
    companies: Array<string>,
    groups: Array<string>,
    months: Array<number>,
    year: number,
    outputType: OutputType,
    showDetails: boolean,
    showActiveness: boolean,
    showTimesheet: boolean,
    showBusinessTrip: boolean,
    showPrintout: boolean,
    showBasic: boolean,
    showPRO: boolean,
    showVPN: boolean,
    showUpdater: boolean,
    showCurrentlyActive: boolean,
  ) => any
  onClose: () => void
}

/**
 * Wydruk aktywnych kierowców - funkcyjny odpowiednik `printout/activeDriversModal.tsx`.
 *
 * Rok wybiera się listą zamiast `DayPickerInput` z ręcznie rysowaną nakładką siatki lat
 * (dziewięć lat wokół bieżącego, dokładnie tyle co dawna siatka 3x3).
 */
export const ActiveDriversModal: React.FC<Props> = ({
  currentCompanyId,
  companies,
  groups,
  getCompanies,
  getGroups,
  getActiveDriversReport,
  onClose,
}) => {
  const { t } = useLocalize()

  const [selectedMonths, setSelectedMonths] = React.useState<number[]>([
    moment().month() + 1,
  ])
  const [year, setYear] = React.useState(moment().year())
  const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([])
  const [selectedGroups, setSelectedGroups] = React.useState<string[]>([])
  const [isCsv, setIsCsv] = React.useState(false)
  const [detailedReport, setDetailedReport] = React.useState(false)
  const [showActiveness, setShowActiveness] = React.useState(true)
  const [showTimesheet, setShowTimesheet] = React.useState(false)
  const [showBusinessTrip, setShowBusinessTrip] = React.useState(false)
  const [showPrintout, setShowPrintout] = React.useState(false)
  const [showBasic, setShowBasic] = React.useState(false)
  const [showPRO, setShowPRO] = React.useState(true)
  const [showVPN, setShowVPN] = React.useState(false)
  const [showUpdater, setShowUpdater] = React.useState(true)
  const [showCurrentlyActive, setShowCurrentlyActive] = React.useState(true)

  const years = React.useMemo(() => {
    const current = moment().year()
    return [0, 1, 2, 3, 4, 5, 6, 7, 8].map((offset) => current - 4 + offset)
  }, [])

  React.useEffect(() => {
    getCompanies(currentCompanyId, true)
    getGroups(currentCompanyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompanyId])

  const allCompanies = React.useMemo(() => {
    const result: CompanyDetailsModel[] = []
    for (const company of companies) {
      result.push({ ...company })
      for (const child of company.childCompanies ?? []) {
        if (!isAdmin() && child.tenantId !== currentCompanyId) continue
        result.push({ ...child })
      }
    }
    return result
  }, [companies, currentCompanyId])

  React.useEffect(() => {
    setSelectedCompanies(allCompanies.map((company) => String(company.id)))
  }, [allCompanies])

  const toggleMonth = (month: number) =>
    setSelectedMonths((prev) =>
      prev.includes(month)
        ? prev.filter((item) => item !== month)
        : [...prev, month],
    )

  return (
    <AdminModal
      title={t(
        'administrationPanel-companies.generate-active-drivers-report-title',
      )}
      width={760}
      onClose={onClose}
      onConfirm={() => {
        onClose()
        getActiveDriversReport(
          selectedCompanies,
          selectedGroups,
          selectedMonths,
          year,
          isCsv ? OutputType.Csv : OutputType.Pdf,
          detailedReport,
          showActiveness,
          showTimesheet,
          showBusinessTrip,
          showPrintout,
          showBasic,
          showPRO,
          showVPN,
          showUpdater,
          showCurrentlyActive,
        )
      }}
    >
      <AdminModalRow label={t('audit-report.companies')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          searchPlaceholder={t(
            'administrationPanel-users.filter-find-placeholder',
          )}
          placeholder={t('administrationPanel-users.filter-companies')}
          options={allCompanies.map((company) => ({
            value: String(company.id),
            label: company.companyName,
            content: (
              <span className="tw-flex tw-items-center tw-gap-2">
                <AdminIcon
                  name={getCompanyTypeIcon(
                    company.type,
                    company.accountingOnly,
                  )}
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-flex tw-flex-col">
                  <span>{company.companyName}</span>
                  {company.tenant != null && (
                    <span className="tw-text-xs tw-opacity-70">
                      {company.tenant.companyName}
                    </span>
                  )}
                </span>
              </span>
            ),
          }))}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
        />
      </AdminModalRow>

      <AdminModalRow label={t('audit-report.groups')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          placeholder={t('administrationPanel-users.choose-group')}
          options={groups.map((group) => ({
            value: String(group.id),
            label: group.name ?? '',
          }))}
          value={selectedGroups}
          onChange={setSelectedGroups}
        />
      </AdminModalRow>

      <AdminModalRow label={t('administrationPanel-companies.year')}>
        <Dropdown
          compact
          portalMenu
          value={String(year)}
          options={years.map((item) => ({
            value: String(item),
            label: String(item),
          }))}
          onChange={(value) => setYear(Number(value))}
        />
      </AdminModalRow>

      <AdminModalRow label={t('administrationPanel-companies.months')}>
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {MONTHS.map((label, index) => {
            const month = index + 1
            const selected = selectedMonths.includes(month)
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleMonth(month)}
                className={`tw-h-8 tw-w-8 tw-rounded tw-border-2 tw-border-primary tw-text-xs ${
                  selected
                    ? 'tw-bg-primary tw-text-white'
                    : 'tw-bg-white tw-text-primary'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
      </AdminModalRow>

      <AdminModalRow label={t('administrationPanel-companies.companies')}>
        <div className="tw-flex tw-flex-wrap tw-gap-6">
          <CheckboxField
            id="show-PRO"
            checked={showPRO}
            label={t('administrationPanel-companies.show-PRO')}
            onChange={() => setShowPRO((value) => !value)}
          />
          <CheckboxField
            id="show-basic"
            checked={showBasic}
            label={t('administrationPanel-companies.show-basic')}
            onChange={() => setShowBasic((value) => !value)}
          />
        </div>
      </AdminModalRow>

      <AdminModalRow label={t('administrationPanel-companies.source')}>
        <div className="tw-flex tw-flex-wrap tw-gap-6">
          <CheckboxField
            id="show-VPN"
            checked={showVPN}
            label={t('administrationPanel-companies.show-VPN')}
            onChange={() => setShowVPN((value) => !value)}
          />
          <CheckboxField
            id="show-Updater"
            checked={showUpdater}
            label={t('administrationPanel-companies.show-Updater')}
            onChange={() => setShowUpdater((value) => !value)}
          />
        </div>
      </AdminModalRow>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-pl-44">
        <CheckboxField
          id="show-activeness"
          checked={showActiveness}
          label={t('administrationPanel-companies.show-activeness')}
          onChange={() => setShowActiveness((value) => !value)}
        />
        <CheckboxField
          id="show-timesheet"
          checked={showTimesheet}
          label={t('administrationPanel-companies.show-timesheet')}
          onChange={() => setShowTimesheet((value) => !value)}
        />
        <CheckboxField
          id="show-businessTrip"
          checked={showBusinessTrip}
          label={t('administrationPanel-companies.show-businessTrip')}
          onChange={() => setShowBusinessTrip((value) => !value)}
        />
        <CheckboxField
          id="show-printout"
          checked={showPrintout}
          label={t('administrationPanel-companies.show-printout')}
          onChange={() => setShowPrintout((value) => !value)}
        />
        <CheckboxField
          id="currentlyActive"
          checked={showCurrentlyActive}
          label={t('administrationPanel-companies.currently-active')}
          onChange={() => setShowCurrentlyActive((value) => !value)}
        />
        <CheckboxField
          id="detailedReport"
          checked={detailedReport}
          label={t('administrationPanel-companies.detailed-report')}
          onChange={() => setDetailedReport((value) => !value)}
        />
        <CheckboxField
          id="isCsv"
          checked={isCsv}
          label={t('administrationPanel-companies.isCsv')}
          onChange={() => setIsCsv((value) => !value)}
        />
      </div>
    </AdminModal>
  )
}
